import { useRef, useState } from 'react';
import { FileUp, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button, Field, Input, Section, Select } from '@zt/ui';
import { ProductMultiPicker } from '../components/ProductMultiPicker';
import { api, ApiError } from '../lib/api';
import { HelpTip } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { usePermissions } from '../lib/session';
import type { ProductDetail } from '../lib/types';

/**
 * Ürün sayfası ekleri (F5B-05): videolar (YouTube/Vimeo adresi), PDF belgeler (analiz sertifikası vb.) ve
 * elle seçilen tamamlayıcı ürünler. Değerler ürün formuyla birlikte "Kaydet" ile gönderilir.
 */

export const DOCUMENT_KINDS = [
  'analysis_certificate',
  'leaflet',
  'safety_data_sheet',
  'registration',
  'other',
] as const;
export type DocumentKind = (typeof DOCUMENT_KINDS)[number];

export interface ExtrasForm {
  videos: Array<{ url: string; title: string }>;
  documents: Array<{ fileId: string; title: string; kind: DocumentKind; name: string; sizeBytes: number }>;
  complementaryIds: string[];
}

const watchUrl = (v: { provider: 'youtube' | 'vimeo'; videoId: string }) =>
  v.provider === 'youtube'
    ? `https://www.youtube.com/watch?v=${v.videoId}`
    : `https://vimeo.com/${v.videoId}`;

export function extrasFromDetail(p: ProductDetail | null): ExtrasForm {
  if (!p) return { videos: [], documents: [], complementaryIds: [] };
  return {
    videos: (p.videos ?? []).map((v) => ({ url: watchUrl(v), title: v.title })),
    documents: (p.documents ?? []).map((d) => {
      const f = p.documentFiles.find((x) => x.id === d.fileId);
      return { ...d, name: f?.originalName ?? '', sizeBytes: f?.sizeBytes ?? 0 };
    }),
    complementaryIds: p.complementaryIds ?? [],
  };
}

export function extrasBody(f: ExtrasForm) {
  return {
    videos: f.videos
      .filter((v) => v.url.trim() || v.title.trim())
      .map((v) => ({ url: v.url.trim(), title: v.title.trim() })),
    documents: f.documents.map((d) => ({ fileId: d.fileId, title: d.title.trim(), kind: d.kind })),
    complementaryIds: f.complementaryIds,
  };
}

export function ProductExtrasSection({
  value: f,
  onChange,
  disabled,
  error,
  productId,
}: {
  value: ExtrasForm;
  onChange: (next: ExtrasForm) => void;
  disabled: boolean;
  error: (field: string) => string | undefined;
  productId: string | null;
}) {
  const t = useT();
  const { formatNumber } = useI18n();
  const { can } = usePermissions();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const canUpload = !disabled && can('org.file.upload');
  const setVideo = (i: number, patch: Partial<ExtrasForm['videos'][number]>) =>
    onChange({ ...f, videos: f.videos.map((v, j) => (j === i ? { ...v, ...patch } : v)) });
  const setDoc = (i: number, patch: Partial<ExtrasForm['documents'][number]>) =>
    onChange({ ...f, documents: f.documents.map((d, j) => (j === i ? { ...d, ...patch } : d)) });

  return (
    <Section
      title={t('admin.products.extrasTitle')}
      description={t('admin.products.extrasLead')}
      action={<HelpTip k="products.extras" />}
    >
      <div className="space-y-6">
        <fieldset className="space-y-3">
          <legend className="flex items-center gap-1 text-sm font-semibold">
            {t('admin.products.videos')} <HelpTip k="products.videos" />
          </legend>
          {f.videos.map((v, i) => (
            <div key={i} className="grid gap-2 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
              <Field label={t('admin.products.videoUrl', { n: i + 1 })} error={error(`videos.${i}.url`)}>
                {(p) => (
                  <Input
                    {...p}
                    type="url"
                    inputMode="url"
                    value={v.url}
                    disabled={disabled}
                    placeholder="https://www.youtube.com/watch?v=…"
                    onChange={(e) => setVideo(i, { url: e.target.value })}
                  />
                )}
              </Field>
              <Field label={t('admin.products.videoTitle', { n: i + 1 })} error={error(`videos.${i}.title`)}>
                {(p) => (
                  <Input
                    {...p}
                    value={v.title}
                    maxLength={120}
                    disabled={disabled}
                    onChange={(e) => setVideo(i, { title: e.target.value })}
                  />
                )}
              </Field>
              {!disabled && (
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={t('admin.products.removeVideo', { n: i + 1 })}
                  onClick={() => onChange({ ...f, videos: f.videos.filter((_, j) => j !== i) })}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              )}
            </div>
          ))}
          {!disabled && f.videos.length < 5 && (
            <span className="flex items-center gap-1">
              <Button
                size="sm"
                onClick={() => onChange({ ...f, videos: [...f.videos, { url: '', title: '' }] })}
              >
                <Plus className="h-4 w-4" aria-hidden />
                {t('admin.products.addVideo')}
              </Button>
              <HelpTip k="products.video_add" />
            </span>
          )}
        </fieldset>

        <fieldset className="space-y-3">
          <legend className="flex items-center gap-1 text-sm font-semibold">
            {t('admin.products.documents')} <HelpTip k="products.documents" />
          </legend>
          {f.documents.length === 0 && (
            <p className="text-sm text-ink-3">{t('admin.products.noDocuments')}</p>
          )}
          {f.documents.map((d, i) => (
            <div key={d.fileId} className="grid gap-2 sm:grid-cols-[1fr_14rem_auto] sm:items-end">
              <Field
                label={t('admin.products.documentTitle', { n: i + 1 })}
                description={t('admin.products.documentFile', {
                  name: d.name || '—',
                  size: formatNumber(Math.max(1, Math.round(d.sizeBytes / 1024))),
                })}
                error={error(`documents.${i}.title`) ?? error(`documents.${i}.fileId`)}
              >
                {(p) => (
                  <Input
                    {...p}
                    value={d.title}
                    maxLength={120}
                    disabled={disabled}
                    onChange={(e) => setDoc(i, { title: e.target.value })}
                  />
                )}
              </Field>
              <Field label={t('admin.products.documentKind', { n: i + 1 })}>
                {(p) => (
                  <Select
                    {...p}
                    value={d.kind}
                    disabled={disabled}
                    onChange={(e) => setDoc(i, { kind: e.target.value as DocumentKind })}
                  >
                    {DOCUMENT_KINDS.map((k) => (
                      <option key={k} value={k}>
                        {t(`admin.products.documentKinds.${k}`)}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <span className="flex items-center gap-1">
                <a
                  className="text-sm text-pine underline"
                  href={`/v1/files/${d.fileId}/content`}
                  target="_blank"
                  rel="noopener"
                >
                  {t('admin.products.openDocument')}
                </a>
                {!disabled && (
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={t('admin.products.removeDocument', { n: i + 1 })}
                    onClick={() => onChange({ ...f, documents: f.documents.filter((_, j) => j !== i) })}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </Button>
                )}
              </span>
            </div>
          ))}
          {canUpload && f.documents.length < 10 && (
            <div className="flex items-center gap-1">
              <input
                ref={input}
                type="file"
                accept="application/pdf"
                aria-label={t('admin.products.addDocument')}
                tabIndex={-1}
                className="sr-only"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setBusy(true);
                  try {
                    const fd = new FormData();
                    fd.append('file', file);
                    const r = await api.upload<{ id: string; originalName: string; sizeBytes: number }>(
                      '/v1/admin/files/documents',
                      fd,
                    );
                    onChange({
                      ...f,
                      documents: [
                        ...f.documents,
                        {
                          fileId: r.id,
                          title: file.name.replace(/\.pdf$/i, '').slice(0, 120),
                          kind: 'analysis_certificate',
                          name: r.originalName,
                          sizeBytes: r.sizeBytes,
                        },
                      ],
                    });
                    toast.success(t('admin.products.documentUploaded'));
                  } catch (err) {
                    toast.error(err instanceof ApiError ? err.title : String(err));
                  } finally {
                    setBusy(false);
                    if (input.current) input.current.value = '';
                  }
                }}
              />
              <Button size="sm" loading={busy} onClick={() => input.current?.click()}>
                <FileUp className="h-4 w-4" aria-hidden />
                {t('admin.products.addDocument')}
              </Button>
              <HelpTip k="products.document_upload" />
            </div>
          )}
        </fieldset>

        <ProductMultiPicker
          value={f.complementaryIds.filter((id) => id !== productId)}
          onChange={(ids) => onChange({ ...f, complementaryIds: ids })}
          label={t('admin.products.complementary')}
          help={<HelpTip k="products.complementary" />}
          error={error('complementaryIds')}
          max={12}
        />
      </div>
    </Section>
  );
}
